import React, { useEffect, useRef, useState } from 'react'
import {
	ScrollView,
	Platform,
	Dimensions,
	StyleSheet,
	Modal,
	KeyboardAvoidingView,
	Image,
	TextInput as RNTextInput,
} from 'react-native'
import GlobalStyle, {
	lTextSize,
	MAX_CHECKOUT_HEIGHT,
	MAX_CHECKOUT_WIDTH,
} from '../../../constants/GlobalStyle'
import CheckoutProgressBar from '../Components/ProgressBar'
import { ForwardButton } from '../../../components/Headers/UIButtons'
import ReviewOrder from './PlaceOrder/ReviewOrder'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import Colors from '../../../constants/Colors'
import { auth } from '../../../firebase/config'
import { ConfirmOrder } from './PlaceOrder/ConfirmOrder'
import BasicInfo from './PlaceOrder/BasicInfo'
import {
	resetModalTimestamps,
	setCheckoutState,
	updateModalTimestamp,
} from '../../../state/Slices/checkoutSlice'
import { View, Text } from '../../../components/Themed'
import { ChoosePayment } from './PlaceOrder/ChoosePayment'
import { setUserState } from '../../../state/Slices/userSlice'
import { AwaitingPayment } from './PlaceOrder/AwaitingPayment'
import GiftCardOrderConfirmed from '../../GiftCards/views/GiftCardOrderConfirmed'
import { showAlert } from '../../../state/Slices/settingsSlice'

import { RadioInput } from '../../../components/DesignSystem/RadioInput'
import { TipAmountButtons } from '../Components/TipAmountButtons'
import CustomTipInput from '../Components/CutomTipInput'
import { Box, Stack } from '../../../components/DesignSystem'
import OrderTipSummary from '../Components/OrderTipSummary'

type TipFuncDataType = {
	tip0: () => void
	tip5: () => void
	tip10: () => void
	tip12?: () => void
	tip15: () => void
	tip18?: () => void
	tip20?: () => void
	tipPercentage?: (value: number) => void
	tipFixed?: (value: number) => void
	setCustomTip?: (amount: number) => void
	closeModal?: () => void
}

type CheckoutModalProps = {
	toggle: () => void
	goHome?: () => void
	navigation: any
	promoFuncData: () => void
	prices: any
	tipFunctions: TipFuncDataType
	hideOptions?: boolean
	useProductCheckout?: boolean
}

const SCREENS = {
	TIP: 1,
	RECEIPT: 2,
	REVIEW: 3,
	PAYMENT: 4,
}

const SCREEN_NAMES = ['AddTip', 'SelectReceipt', 'ReviewOrder', 'ChoosePayment']

const CONTENT_MARGIN = 100

const CheckoutModal = ({
	toggle,
	goHome,
	navigation,
	promoFuncData,
	prices,
	tipFunctions,
	hideOptions = false,
	useProductCheckout = false,
}: CheckoutModalProps) => {
	const {
		isConfirmModalVisible,
		spinner,
		orderPlaced,
		isDigitalWalletEnabled,
		digitalWalletType,
		awaitingTerminalInput,
		paymentError,
		items,
	} = useAppSelector(state => state.checkout)
	const { paymentMethods, paymentMethodId } = useAppSelector(state => state.user)
	const { appDisplayOptions } = useAppSelector(state => state.settings)
	const { showTipRestaurant } = appDisplayOptions
	const { isDelivery } = useAppSelector(state => state.persisted)
	const [currentScreen, setCurrentScreen] = useState(1)
	const [hasAttemptedNext, setHasAttemptedNext] = useState(false)
	const [allowNext, setAllowNext] = useState(false)
	const [showSavedPayments, setShowSavedPayments] = useState(true)
	const [contentHeight, setContentHeight] = useState(0)
	const window = Dimensions.get('window')
	const isSmallScreen = window.width < 768
	const windowHeight = !isSmallScreen ? window.height * 0.9 : window.height
	const dispatch = useAppDispatch()

	const scrollViewRef = useRef(null)

	useEffect(() => {
		if (isConfirmModalVisible) {
			if (scrollViewRef.current) {
				scrollViewRef.current.scrollTo({ y: 0, animated: false })
			}
			dispatch(
				setCheckoutState({
					useNewPaymentMethod: !(
						auth.currentUser &&
						paymentMethods.length > 0 &&
						showSavedPayments
					),
				})
			)
			const screen = useProductCheckout
				? SCREENS.RECEIPT
				: isDelivery || showTipRestaurant
					? SCREENS.TIP
					: SCREENS.RECEIPT
			setCurrentScreen(screen)
			dispatch(updateModalTimestamp({ screenName: SCREEN_NAMES[screen] }))
		} else {
			dispatch(resetModalTimestamps())
			dispatch(
				setCheckoutState({
					orderNote: '',
					showOrderNotes: false,
					takeOutName: '',
				})
			)
		}
	}, [isConfirmModalVisible])

	const handleToggle = () => {
		toggle()
	}

	const handleNext = () => {
		if (currentScreen !== SCREENS.RECEIPT || allowNext) {
			setCurrentScreen(prevScreen => (prevScreen < 4 ? prevScreen + 1 : prevScreen))
			dispatch(
				updateModalTimestamp({
					screenName: SCREEN_NAMES[currentScreen],
				})
			)
		} else {
			setHasAttemptedNext(true)
		}
	}

	const handleBack = () => {
		if (useProductCheckout && currentScreen === SCREENS.RECEIPT) return toggle()

		if (
			showSavedPayments &&
			isDigitalWalletEnabled &&
			paymentMethodId === digitalWalletType &&
			paymentMethods.length <= 0
		) {
			dispatch(setCheckoutState({ useNewPaymentMethod: true, selectedCard: '' }))
			dispatch(setUserState({ paymentMethodId: '' }))
			setShowSavedPayments(false)
		} else if (
			currentScreen === SCREENS.PAYMENT &&
			auth.currentUser &&
			paymentMethods.length > 0 &&
			!showSavedPayments
		) {
			dispatch(setCheckoutState({ useNewPaymentMethod: false }))
			setShowSavedPayments(true)
		} else if (currentScreen === 1 || ((global.kiosk || !isDelivery) && currentScreen === 2)) {
			toggle()
		} else {
			setCurrentScreen(prevScreen => (prevScreen > 1 ? prevScreen - 1 : prevScreen))
		}
	}

	const renderScreen = () => {
		if (paymentError && global.kiosk)
			return <AwaitingPayment title={'Payment error. Please cancel and try again.'} />
		else if (awaitingTerminalInput && global.kiosk)
			return <AwaitingPayment title={'Please follow instructions on pinpad to pay now.'} />
		else if (spinner) return <PlacingOrder />

		switch (currentScreen) {
			case SCREENS.TIP:
				return <TipComponent prices={prices} tipFunctions={tipFunctions} isDelivery={isDelivery} />
			case SCREENS.RECEIPT:
				return (
					<ReceiptComponent
						navigation={navigation}
						toggle={handleToggle}
						hasAttemptedNext={hasAttemptedNext}
						setAllowNext={setAllowNext}
					/>
				)
			case SCREENS.REVIEW:
				return (
					<ReviewComponent
						promoFuncData={promoFuncData}
						prices={prices}
						hideOptions={hideOptions}
						useProductCheckout={useProductCheckout}
					/>
				)
			case SCREENS.PAYMENT:
				return <></>
			default:
				return null
		}
	}

	const modalContent = (
		<KeyboardAvoidingView
			behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
			style={[styles.container, !global.kiosk && !isSmallScreen && styles.desktopContainer]}
		>
			<View style={styles.safeArea}>
				<ScrollView
					ref={scrollViewRef}
					contentContainerStyle={styles.scrollContent}
					keyboardShouldPersistTaps="handled"
					onContentSizeChange={(w, h) => setContentHeight(h)}
				>
					<View style={styles.content}>
						<Text style={styles.screenNumber}>
							{`${useProductCheckout || (!showTipRestaurant && !isDelivery) ? currentScreen - 1 : currentScreen}. ${getScreenTitle(awaitingTerminalInput && global.kiosk ? currentScreen + 1 : currentScreen)}`}
						</Text>
						{renderScreen()}
						<View
							style={{
								display:
									currentScreen === SCREENS.PAYMENT && !orderPlaced && !spinner ? 'flex' : 'none',
							}}
						>
							<PaymentComponent
								navigation={navigation}
								toggle={handleToggle}
								showSavedPayments={showSavedPayments}
								setShowSavedPayments={setShowSavedPayments}
							/>
						</View>
					</View>
				</ScrollView>
				<View
					style={[
						styles.disclaimerContainer,
						{
							borderTopWidth: contentHeight + CONTENT_MARGIN >= windowHeight ? 1.5 : 0,
							borderTopColor: Colors.greyscale[4],
							display:
								currentScreen === SCREENS.PAYMENT && !orderPlaced && !spinner ? 'flex' : 'none',
						},
					]}
				>
					<Text style={styles.disclaimer}>
						{'Last Step! Press "Pay Now" to charge your card and send your order.'}
					</Text>
				</View>
				{!spinner && !orderPlaced && (
					<CheckoutProgressBar
						filled={global.kiosk || !isDelivery ? currentScreen - 1 : currentScreen}
						total={global.kiosk ? 2 : isDelivery ? 4 : 3}
					/>
				)}

				<View style={styles.footerRow}>
					{(currentScreen == SCREENS.REVIEW && prices.total === 0 && !orderPlaced) ||
					((currentScreen === SCREENS.PAYMENT ||
						(global.kiosk && currentScreen == SCREENS.REVIEW)) &&
						!spinner &&
						!orderPlaced) ? (
						<ConfirmOrder
							navigation={navigation}
							toggleConfirmModal={toggle}
							handleBack={handleBack}
							useProductCheckout={useProductCheckout} // Defaults to classic order checkout.
							pricesRef={prices}
						/>
					) : !spinner && !orderPlaced ? (
						<>
							<View style={{ flex: 1, maxWidth: '33%' }}>
								<ForwardButton onPress={handleBack} color={'white'} title={'BACK'} />
							</View>
							<View style={{ flex: 1, marginLeft: 20, maxWidth: '33%' }}>
								<ForwardButton
									onPress={handleNext}
									color={global.orgColor}
									opacity={0.9}
									title={'NEXT'}
								/>
							</View>
						</>
					) : null}
				</View>
			</View>
		</KeyboardAvoidingView>
	)

	if (isSmallScreen) {
		return (
			<Modal
				visible={isConfirmModalVisible}
				animationType="slide"
				presentationStyle="fullScreen"
				statusBarTranslucent={true}
				transparent
			>
				{modalContent}
			</Modal>
		)
	}

	return (
		<Modal
			visible={isConfirmModalVisible}
			animationType="fade"
			statusBarTranslucent={true}
			transparent
		>
			<View style={styles.desktopOverlay}>{modalContent}</View>
		</Modal>
	)
}

// Screen Components
const TipComponent = ({ prices, tipFunctions, isDelivery }) => {
	const { items } = useAppSelector(state => state.checkout)
	const { restaurants } = useAppSelector(state => state.data)
	const restaruantList = items.map(item => restaurants.find(r => r.id === item.rId))
	const titleForPickup = restaruantList.length > 1 ? 'the restaurants' : restaruantList[0]?.name

	const [presetRadioOn, setPresetRadioOn] = useState<boolean | null>(true)
	const customTipInputRef = useRef<RNTextInput>(null)

	return (
		<View style={{ marginHorizontal: 0 }}>
			<Text
				style={[GlobalStyle.subHeaderText, { marginTop: 15, marginBottom: 30, textAlign: 'left' }]}
			>
				{`ADD A TIP FOR ${isDelivery ? 'YOUR DRIVER' : titleForPickup?.toUpperCase()}`}
			</Text>
			<Stack gap="xxl" align="left">
				<Stack gap="sm">
					<RadioInput
						label="Preset"
						selected={presetRadioOn}
						onPress={() => {
							setPresetRadioOn(true)
							tipFunctions.tip0()
						}}
					/>
					<TipAmountButtons
						prices={prices}
						tipFuncData={tipFunctions}
						isDelivery={isDelivery}
						onFocus={() => setPresetRadioOn(true)}
						isCustomTipFocused={!presetRadioOn}
					/>
				</Stack>
				<Stack gap="sm" align="left">
					<RadioInput
						label="Custom"
						selected={presetRadioOn === false}
						onPress={() => {
							setPresetRadioOn(false)
							customTipInputRef.current?.focus()
						}}
					/>
					<Box style={{ maxWidth: 160 }}>
						<CustomTipInput
							ref={customTipInputRef}
							presetRadioOn={presetRadioOn}
							prices={prices}
							setCustomTip={tipFunctions.setCustomTip}
							onFocus={() => setPresetRadioOn(false)}
						/>
					</Box>
				</Stack>
				<OrderTipSummary prices={prices} />
			</Stack>
		</View>
	)
}

const ReceiptComponent = ({ navigation, toggle, hasAttemptedNext, setAllowNext }) => (
	<View style={styles.screenContainer}>
		<BasicInfo
			navigation={navigation}
			toggleConfirmModal={toggle}
			hasAttemptedNext={hasAttemptedNext}
			setAllowNext={setAllowNext}
		/>
	</View>
)

const ReviewComponent = ({ promoFuncData, prices, hideOptions, useProductCheckout }) => (
	<View style={[styles.screenContainer, { justifyContent: 'flex-start' }]}>
		<ReviewOrder
			promoFuncData={promoFuncData}
			prices={prices}
			hideOptions={hideOptions}
			useProductCheckout={useProductCheckout}
		/>
	</View>
)

const PaymentComponent = ({ navigation, toggle, showSavedPayments, setShowSavedPayments }) => (
	<View style={[styles.screenContainer, { justifyContent: 'flex-start' }]}>
		<ChoosePayment
			navigation={navigation}
			toggle={toggle}
			showSavedPayments={showSavedPayments}
			setShowSavedPayments={setShowSavedPayments}
		/>
	</View>
)

const PlacingOrder = () => {
	const dispatch = useAppDispatch()
	useEffect(() => {
		dispatch(updateModalTimestamp({ screenName: 'PlacingOrder' }))
	}, [])
	return (
		<View style={[styles.screenContainer, { justifyContent: 'center' }]}>
			<Text style={GlobalStyle.subHeaderText}>Your order is being placed...</Text>
			<View style={{ alignItems: 'center', justifyContent: 'center' }}>
				<Image
					style={{
						width: 100,
						height: 100,
					}}
					source={require('../../../assets/images/loadImg.gif')}
				/>
			</View>
		</View>
	)
}

const getScreenTitle = screen => {
	switch (screen) {
		case SCREENS.TIP:
			return 'TIP OPTIONS'
		case SCREENS.RECEIPT:
			return 'BASIC INFORMATION'
		case SCREENS.REVIEW:
			return 'REVIEW ORDER'
		case SCREENS.PAYMENT:
			return 'PAYMENT'
		default:
			return ''
	}
}

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: 'white',
		width: '100%',
	},
	desktopContainer: {
		maxWidth: MAX_CHECKOUT_WIDTH * 0.8,
		maxHeight: MAX_CHECKOUT_HEIGHT * 0.9,
		borderRadius: 12,
		overflow: 'hidden',
		width: '60%',
		height: '90%',
	},
	disclaimerContainer: {
		alignItems: 'flex-start',
	},
	desktopOverlay: {
		flex: 1,
		backgroundColor: 'rgba(0, 0, 0, 0.7)',
		justifyContent: 'center',
		alignItems: 'center',
	},
	safeArea: {
		flex: 1,
		paddingTop: 10,
	},
	scrollContent: {
		flexGrow: 1,
	},
	content: {
		flex: 1,
		padding: 25,
	},
	totalText: {
		color: 'black',
		fontWeight: '500',
		fontSize: lTextSize,
		letterSpacing: 1,
		flex: 0.5,
		alignSelf: 'flex-start',
		marginTop: 20,
	},
	screenNumber: {
		fontSize: 24,
		fontWeight: 'bold',
		marginBottom: 25,
		textAlign: 'left',
		letterSpacing: 1.1,
	},
	screenContainer: {
		flex: 1,
		justifyContent: 'space-between',
	},
	footerRow: {
		flexDirection: 'row',
		paddingVertical: 15,
		paddingHorizontal: 25,
		justifyContent: 'space-between',
	},
	disclaimer: {
		color: Colors.greyscale[7],
		textAlign: 'left',
		marginVertical: 25,
		marginHorizontal: '5%',
	},
})

export default CheckoutModal
